import React from 'react';
import * as Sentry from '@sentry/browser';
import { makeStyles } from '@material-ui/core/styles';
import compact from 'lodash/compact';
import { useMutation } from '@apollo/react-hooks';
import Grid from '@material-ui/core/Grid';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import IconButton from '@material-ui/core/IconButton';
import Button from 'components/common/Button';
import InfoIcon from '@material-ui/icons/InfoOutlined';
import CloseIcon from '@material-ui/icons/Close';
import Form from 'components/form';
import Input from 'components/form/Input';
import Select from 'components/form/Select';
import useSnack from 'hooks/useSnack';
import { TOPIC_TYPES } from 'constant/essay';
import { ADD_TOPIC, EDIT_TOPIC, GET_ESSAY_TOPICS } from 'graphql/essays';
import ScoreInput from './ScoreInput';

const useStyles = makeStyles((theme) => ({
  mainGrid: {
    'padding': theme.spacing(5),
    '& label': {
      fontWeight: 'bold',
    },
  },
  infoIcon: {
    display: 'block',
    fontSize: 70,
    opacity: 0.7,
    marginBottom: theme.spacing(2),
  },
  closeIcon: {
    position: 'absolute',
    right: theme.spacing(1),
    top: theme.spacing(1),
    color: theme.palette.grey[500],
  },
  paperWidthLg: {
    maxWidth: 1100,
  },
}));

const { FormItem } = Form;

const wordCountValidator = (rule, value, cb) => {
  if (value) {
    const count = compact(value.split(' ')).length;
    if (count > rule.limit) {
      cb(new Error(`Word limit: ${rule.limit}`));
    } else {
      cb();
    }
  } else {
    cb();
  }
};

const TopicFormModal = ({ onClose, form, student, topic, matches }) => {
  const classes = useStyles();
  const [addTopic] = useMutation(ADD_TOPIC);
  const [updateTopic] = useMutation(EDIT_TOPIC);
  const snack = useSnack();
  const { getFieldDecorator, validateFields } = form;

  const handleSubmit = () => {
    return validateFields().then((values) => {
      if (topic) {
        return updateTopic({
          variables: {
            id: topic.id,
            data: values,
          },
        }).then(
          () => {
            snack.success('Topic updated');
            onClose();
          },
          (e) => snack.error(e.message),
        );
      }
      return addTopic({
        variables: {
          data: {
            ...values,
            user_id: student.id,
          },
        },
        update: (proxy, mutationResult) => {
          const result = proxy.readQuery({
            query: GET_ESSAY_TOPICS,
            variables: {
              userId: student.id,
            },
          });

          if (result) {
            result.essay_topics.push(mutationResult.data.row);
          }
          proxy.writeQuery({
            query: GET_ESSAY_TOPICS,
            variables: { userId: student.id },
            data: {
              essay_topics: result
                ? result.essay_topics
                : [mutationResult.data.row],
            },
          });
        },
      }).then(
        () => {
          snack.success('Topic added');
          onClose();
        },
        (e) => {
          Sentry.captureException(e);
          snack.error(e.message);
        },
      );
    });
  };
  return (
    <Dialog
      open
      disableBackdropClick
      fullWidth
      maxWidth={'sm'}
      classes={{
        paperWidthLg: classes.paperWidthLg,
      }}
    >
      <DialogTitle>
        <Box>
          <Typography variant="subtitle1">
            {topic ? 'Edit Essay Topic' : 'Add Essay Topic'}
          </Typography>
          <Typography variant="caption">
            Think about something you proud of.
          </Typography>
          <IconButton
            className={classes.closeIcon}
            onClick={onClose}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Form>
          <div>
            <FormItem>
              {getFieldDecorator('idea', {
                initialValue: topic?.idea,
                rules: [
                  { required: true },
                  { validator: wordCountValidator, limit: 15 },
                ],
              })(
                <Input
                  label="Topic idea and overview (Limit: 15 words)"
                  multiline
                  rows={2}
                />,
              )}
            </FormItem>
            <FormItem>
              {getFieldDecorator('outcome', {
                initialValue: topic?.outcome,
                rules: [
                  { required: true },
                  { validator: wordCountValidator, limit: 5 },
                ],
              })(
                <Input label="Result, achievement, outcome (Limit: 5 words)" />,
              )}
            </FormItem>
            <FormItem>
              {getFieldDecorator('type', {
                initialValue: topic?.type,
                rules: [{ required: true }],
              })(
                <Select
                  transformValue
                  isMulti={false}
                  label="Essay type"
                  options={TOPIC_TYPES}
                />,
              )}
            </FormItem>
            <FormItem>
              {getFieldDecorator('impact', {
                initialValue: topic?.impact,
                rules: [{ required: true }],
              })(<ScoreInput label="Impact" />)}
            </FormItem>
            <FormItem>
              {getFieldDecorator('like', {
                initialValue: topic?.like,
                rules: [{ required: true }],
              })(<ScoreInput label="Like" />)}
            </FormItem>
            <FormItem>
              {getFieldDecorator('ease', {
                initialValue: topic?.ease,
                rules: [{ required: true }],
              })(<ScoreInput label="Ease" />)}
            </FormItem>
            <Box textAlign="center" paddingBottom={2} paddingTop={2}>
              <Button
                size="large"
                color="primary"
                variant="contained"
                onClick={handleSubmit}
              >
                {topic ? 'Update Topic' : 'Add Topic'}
              </Button>
            </Box>
          </div>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default Form.create()(TopicFormModal);
